import React from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { PDFViewer } from '@embedpdf/react-pdf-viewer';
import {
    headerContentStyle,
    modalBodyStyle,
    modalHeaderStyle,
    pdfBasePath,
    tableOfContents,
    tocItemContentStyle,
    tocListStyle,
} from './userManual';
import { TableOfContentsProps, TocItem } from '../../Types/UserManual.type';

const TableOfContents: React.FC<TableOfContentsProps> = ({
    isOpen,
    toggle,
    pdfPath = pdfBasePath,
}) => {
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
    const [pdfOpen, setPdfOpen] = React.useState(false);
    const [targetPage, setTargetPage] = React.useState(1);

    const fullPdfUrl = `${window.location.origin}${pdfPath}`;

    const handleNavigate = (page: number) => {
        setTargetPage(page);
        setPdfOpen(true);
    };

    const handleClosePdf = () => setPdfOpen(false);

    const handleDownload = () => {
        window.open(fullPdfUrl, '_blank', 'noopener,noreferrer');
    };

    const handleReady = async (registry: any) => {
        try {
            const scroll = registry.getPlugin('scroll').provides();
            if (!scroll) return;

            scroll.onLayoutReady((event: any) => {
                if (event.isInitial) {
                    scroll.scrollToPage({ pageNumber: targetPage, behavior: 'instant' });
                }
            });
        } catch (e) {
            // safe to ignore
        }
    };

    const getIndentLevel = (title: string): number => {
        const match = title.match(/^(\d+\.)+/);
        if (!match) return 0;
        return (match[0].match(/\./g) || []).length;
    };

    const getTocItemStyle = (item: TocItem, index: number, indentLevel: number): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            padding: '12px 20px',
            paddingLeft: `${15 + (indentLevel * 20)}px`,
            borderBottom: index === tableOfContents.length - 1 ? 'none' : '1px solid #f0f0f0',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
        };

        const mainSectionStyle: React.CSSProperties = item.isMainSection ? {
            backgroundColor: hoveredIndex === index ? '#f3f4f6' : '#fafbfc',
            fontWeight: 600,
            borderBottom: '2px solid #e1e4e8',
        } : {};

        const hoverStyle: React.CSSProperties = hoveredIndex === index ? {
            backgroundColor: item.isMainSection ? '#f3f4f6' : '#f8f9fa',
            paddingLeft: `${30 + (indentLevel * 20)}px`,
            borderLeft: '3px solid #667eea',
        } : {};

        return { ...baseStyle, ...mainSectionStyle, ...hoverStyle };
    };

    const getTocTitleStyle = (item: TocItem): React.CSSProperties => ({
        fontSize: item.isMainSection ? '15px' : '14px',
        color: item.isMainSection ? '#1a1f36' : '#24292e',
        flex: 1,
    });

    const getTocPageStyle = (item: TocItem): React.CSSProperties => ({
        color: item.isMainSection ? '#5a6875' : '#6a737d',
        fontSize: '13px',
        fontWeight: item.isMainSection ? 600 : 500,
        whiteSpace: 'nowrap',
        marginLeft: '16px',
    });

    return (
        <>
            {/* ── TOC Modal ── */}
            <Modal
                isOpen={isOpen && !pdfOpen}
                toggle={toggle}
                size="lg"
                centered
                contentClassName="toc-modal-content"
            >
                <ModalHeader toggle={toggle} style={modalHeaderStyle}>
                    <div style={headerContentStyle}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22" height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ marginRight: '10px', color: '#1a1f36' }}
                            >
                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                            </svg>
                            <h5 style={{ margin: 0, fontWeight: 600 }}>
                                ACERS User Manual - Table of Contents
                            </h5>
                        </div>
                        <button
                            onClick={handleDownload}
                            style={{
                                background: '#00bcd4',
                                color: '#ffffff',
                                border: 'none',
                                padding: '6px 14px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: '0.2s',
                            }}
                        >
                            Download Full PDF
                        </button>
                    </div>
                </ModalHeader>
                <ModalBody style={modalBodyStyle}>
                    <ul style={tocListStyle}>
                        {tableOfContents.map((item, index) => {
                            const indentLevel = getIndentLevel(item.title);
                            return (
                                <li
                                    key={index}
                                    style={getTocItemStyle(item, index, indentLevel)}
                                    onClick={() => handleNavigate(item.page)}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                >
                                    <div style={tocItemContentStyle}>
                                        <span style={getTocTitleStyle(item)}>{item.title}</span>
                                        <span style={getTocPageStyle(item)}>Page {item.page}</span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </ModalBody>
            </Modal>

            {/* ── PDF Viewer Modal ── */}
            <Modal
                isOpen={pdfOpen}
                toggle={handleClosePdf}
                size="xl"
                centered
                style={{ maxWidth: '92vw' }}
            >
                <ModalHeader style={modalHeaderStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            onClick={handleClosePdf}
                            style={{
                                background: 'none',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                padding: '5px 14px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: '#374151',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            ← Back to Contents
                        </button>
                        <h5 style={{ margin: 0, fontWeight: 600, color: '#1a1f36' }}>
                            ACERS Help Guide - Page {targetPage}
                        </h5>
                    </div>
                </ModalHeader>
                <ModalBody style={{ padding: 0, height: '82vh' }}>
                    {pdfOpen && (
                        <PDFViewer
                            config={{
                                src: fullPdfUrl,
                                theme: { preference: 'light' },
                                tabBar: 'never',
                            }}
                            onReady={handleReady}
                            style={{ width: '100%', height: '100%' }}
                        />
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export default TableOfContents;